// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  DetailInconsistenciesParameters,
  getDetailInconsistencies,
} from '@/report/api/get-detail-inconsistencies';
import { useQuery } from '@tanstack/react-query';

interface UseDetailInconsistenciesOptions
  extends DetailInconsistenciesParameters {
  /** Whether the query should fire. Defaults to true. */
  enabled?: boolean;
}

/**
 * A React hook that manages the state of a request for the detail inconsistencies report
 * for the current user's organization. This hook retrieves assets with detail-related
 * inconsistencies such as missing cost basis, deployment costs without acquisition costs,
 * excessive salvage values, or missing useful life.
 */
export function useDetailInconsistencies(
  options: UseDetailInconsistenciesOptions = {}
) {
  const { enabled = true, ...params } = options;

  return useQuery({
    queryKey: [
      'report',
      'exception',
      'asset',
      'detail-inconsistencies',
      params,
    ],
    queryFn: () => getDetailInconsistencies(params),
    // Disabled: users switch tabs to complete resolution tasks, and we don't
    // want to trigger expensive refetches when they return. Optimistic UI
    // handles the immediate feedback; data syncs on page refresh or navigation.
    refetchOnWindowFocus: false,
    enabled,
  });
}
